import React, { useState, useRef } from "react";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import { 
  Plus, Minus, X, Divide, Equal, 
  Superscript, ChevronRight, 
  Trash2, Send, RotateCcw,
  Square, PenTool, LineChart as LineIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import GraphTool from "./GraphTool";

// Math symbols and operations
const mathSymbols = [
  { label: "+", latex: "+", category: "basic" },
  { label: "−", latex: "-", category: "basic" },
  { label: "×", latex: "\\times", category: "basic" },
  { label: "÷", latex: "\\div", category: "basic" },
  { label: "=", latex: "=", category: "basic" },
  { label: "≠", latex: "\\neq", category: "basic" },
  { label: "<", latex: "<", category: "basic" },
  { label: ">", latex: ">", category: "basic" },
  { label: "≤", latex: "\\leq", category: "basic" },
  { label: "≥", latex: "\\geq", category: "basic" },
  { label: "√", latex: "\\sqrt{}", category: "advanced", cursor: -1 },
  { label: "∛", latex: "\\sqrt[3]{}", category: "advanced", cursor: -1 },
  { label: "x²", latex: "^{2}", category: "advanced" },
  { label: "xⁿ", latex: "^{}", category: "advanced", cursor: -1 },
  { label: "x₁", latex: "_{}", category: "advanced", cursor: -1 },
  { label: "π", latex: "\\pi", category: "constants" },
  { label: "∞", latex: "\\infty", category: "constants" },
  { label: "°", latex: "^{\\circ}", category: "constants" },
  { label: "a/b", latex: "\\frac{}{}", category: "fractions", cursor: -3 },
  { label: "()", latex: "\\left( \\right)", category: "brackets", cursor: -7 },
  { label: "[]", latex: "\\left[ \\right]", category: "brackets", cursor: -7 },
  { label: "±", latex: "\\pm", category: "basic" },
  { label: "∑", latex: "\\sum_{i=1}^{n}", category: "advanced" },
  { label: "∫", latex: "\\int_{a}^{b}", category: "advanced" },
  { label: "lim", latex: "\\lim_{x \\to }", category: "advanced" },
  { label: "log", latex: "\\log_{}", category: "advanced", cursor: -1 },
  { label: "sin", latex: "\\sin", category: "trig" },
  { label: "cos", latex: "\\cos", category: "trig" },
  { label: "tan", latex: "\\tan", category: "trig" },
];

// Greek letters
const greekLetters = [
  { label: "α", latex: "\\alpha" },
  { label: "β", latex: "\\beta" },
  { label: "γ", latex: "\\gamma" },
  { label: "δ", latex: "\\delta" },
  { label: "θ", latex: "\\theta" },
  { label: "λ", latex: "\\lambda" },
  { label: "μ", latex: "\\mu" },
  { label: "σ", latex: "\\sigma" },
  { label: "Δ", latex: "\\Delta" },
  { label: "Ω", latex: "\\Omega" },
];

export const MathWhiteboard = ({ onSubmit, subjectColor = "#3b82f6" }) => {
  const [steps, setSteps] = useState([{ id: 1, latex: "", label: "Paso 1" }]);
  const [activeStep, setActiveStep] = useState(1);
  const [showGreek, setShowGreek] = useState(false);
  const inputRefs = useRef({});

  const addStep = () => {
    const newStep = {
      id: steps.length + 1,
      latex: "",
      label: `Paso ${steps.length + 1}`
    };
    setSteps([...steps, newStep]);
    setActiveStep(newStep.id);
  };

  const updateStep = (id, latex) => {
    setSteps(steps.map(s => s.id === id ? { ...s, latex } : s));
  };

  const removeStep = (id) => {
    if (steps.length > 1) {
      const newSteps = steps.filter(s => s.id !== id).map((s, i) => ({
        ...s,
        id: i + 1,
        label: `Paso ${i + 1}`
      }));
      setSteps(newSteps);
      setActiveStep(Math.min(activeStep, newSteps.length));
    }
  };

  const insertSymbol = (symbol) => {
    const currentStep = steps.find(s => s.id === activeStep);
    if (!currentStep) return;

    const input = inputRefs.current[activeStep];
    if (input) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const currentLatex = currentStep.latex;
      const newLatex = currentLatex.substring(0, start) + symbol.latex + currentLatex.substring(end);
      
      updateStep(activeStep, newLatex);
      
      // Set cursor position
      setTimeout(() => {
        const newPos = start + symbol.latex.length + (symbol.cursor || 0);
        input.focus();
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      updateStep(activeStep, currentStep.latex + symbol.latex);
    }
  };

  const clearAll = () => {
    setSteps([{ id: 1, latex: "", label: "Paso 1" }]);
    setActiveStep(1);
  };

  const handleSubmit = () => {
    const content = steps
      .filter(s => s.latex.trim())
      .map(s => `${s.label}: $${s.latex}$`)
      .join("\n");
    
    if (content.trim()) {
      onSubmit(content, steps);
    }
  };

  // Sends a graph payload to the tutor as if the student wrote a message
  // containing a compact JSON description of the chart.
  const [graphSending, setGraphSending] = useState(false);
  const handleGraphSubmit = (payload) => {
    setGraphSending(true);
    try {
      const json = JSON.stringify(payload);
      const human = `[Gráfico enviado por el estudiante]\n\`\`\`json\n${json}\n\`\`\``;
      onSubmit(human, [{ id: 1, label: "Gráfico", latex: "", graph: payload }]);
    } finally {
      setTimeout(() => setGraphSending(false), 400);
    }
  };

  const currentStep = steps.find(s => s.id === activeStep);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div 
        className="px-4 py-3 border-b border-gray-100 flex items-center justify-between"
        style={{ backgroundColor: `${subjectColor}08` }}
      >
        <div className="flex items-center gap-2">
          <PenTool className="w-5 h-5" style={{ color: subjectColor }} />
          <span className="font-semibold text-gray-800">Pizarra Matemática</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="text-gray-500 hover:text-red-500"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Limpiar
          </Button>
        </div>
      </div>

      {/* Tabs: Ecuaciones | Gráficos */}
      <Tabs defaultValue="ecuaciones" className="w-full">
        <div className="px-4 pt-3 border-b border-gray-100">
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="ecuaciones" data-testid="wb-tab-ecuaciones">
              <PenTool className="w-4 h-4 mr-2" />Ecuaciones
            </TabsTrigger>
            <TabsTrigger value="graficos" data-testid="wb-tab-graficos">
              <LineIcon className="w-4 h-4 mr-2" />Gráficos
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="ecuaciones" className="mt-0">
          {/* Symbol Toolbar */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {mathSymbols.slice(0, 16).map((sym, i) => (
            <button
              key={i}
              onClick={() => insertSymbol(sym)}
              className="w-9 h-9 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 flex items-center justify-center text-sm font-medium text-gray-700 transition-all"
              title={sym.latex}
            >
              {sym.label}
            </button>
          ))}
          <button
            onClick={() => setShowGreek(!showGreek)}
            className={`px-3 h-9 rounded-lg border flex items-center justify-center text-xs font-medium transition-all ${
              showGreek 
                ? "bg-blue-100 border-blue-300 text-blue-700" 
                : "bg-white border-gray-200 text-gray-600 hover:border-blue-300"
            }`}
          >
            αβγ
          </button>
        </div>

        {/* Greek letters */}
        {showGreek && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-200">
            {greekLetters.map((sym, i) => (
              <button
                key={i}
                onClick={() => insertSymbol(sym)}
                className="w-9 h-9 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 flex items-center justify-center text-sm font-medium text-gray-700 transition-all"
              >
                {sym.label}
              </button>
            ))}
          </div>
        )}

        {/* Advanced symbols row */}
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-200">
          {mathSymbols.slice(16).map((sym, i) => (
            <button
              key={i}
              onClick={() => insertSymbol(sym)}
              className="px-2.5 h-8 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 flex items-center justify-center text-xs font-medium text-gray-700 transition-all"
              title={sym.latex}
            >
              {sym.label}
            </button>
          ))}
        </div>
      </div>

      {/* Steps Area */}
      <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
        {steps.map((step) => (
          <div 
            key={step.id}
            className={`rounded-xl border-2 transition-all ${
              activeStep === step.id 
                ? "border-blue-400 bg-blue-50/50" 
                : "border-gray-100 bg-gray-50/50"
            }`}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500">{step.label}</span>
              {steps.length > 1 && (
                <button
                  onClick={() => removeStep(step.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            
            {/* LaTeX Input */}
            <div className="p-3">
              <input
                ref={el => inputRefs.current[step.id] = el}
                type="text"
                value={step.latex}
                onChange={(e) => updateStep(step.id, e.target.value)}
                onFocus={() => setActiveStep(step.id)}
                placeholder="Escribe tu ecuación aquí..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent font-mono"
              />
              
              {/* Preview */}
              {step.latex && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-gray-100 min-h-[50px] flex items-center justify-center">
                  <ErrorBoundary fallback={<span className="text-red-500 text-sm">Error en la fórmula</span>}>
                    <BlockMath math={step.latex} />
                  </ErrorBoundary>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={addStep}
          className="text-gray-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          Agregar paso
        </Button>
        
        <Button
          onClick={handleSubmit}
          size="sm"
          className="text-white"
          style={{ backgroundColor: subjectColor }}
          disabled={!steps.some(s => s.latex.trim())}
        >
          <Send className="w-4 h-4 mr-1" />
          Verificar con el tutor
        </Button>
      </div>
        </TabsContent>

        <TabsContent value="graficos" className="mt-0 p-4">
          <GraphTool onSendToTutor={handleGraphSubmit} isSending={graphSending} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Error boundary for KaTeX rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.children !== this.props.children) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default MathWhiteboard;
